/**
 * These are our generator-compatible networking utilities. These are predominately used in
 * redux, which is where your networking should be taking place. There are exceptions to this,
 * but those exceptions are mostly due to third-party library constraints.
 */

import { call, select } from 'redux-saga/effects';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import ddt from '@wix/da-ddt';
import { configureOptions, configurePuppyOptions, getApiResponse, getFakeResponse } from './base';
import type { XhrRequestOptions } from './types';
import type { AxiosRequestConfig } from 'axios';
import { CANONICAL_XHR_NAPI_ENDPOINT, LOCAL_XHR_NAPI_ENDPOINT } from './constants';

function* runFake(fakeResponse, options) {
  // Using node env so we strip for prod bundle sdf sdf sdf
  if (process.env.NODE_ENV !== 'production') {
    if (fakeResponse !== undefined) {
      const doFake = yield select(getConfigKey, 'fakeApiRequests');
      if (doFake) {
        ddt.log('saga', `Faked XHR for: ${options.url}`, options, fakeResponse(options));
        return yield call(getFakeResponse, fakeResponse(options));
      }
    }
  }
}

function* configureOptionsAndIncludeAuthFromRedux(options: XhrRequestOptions) {
  const withCreds = yield select(getConfigKey, 'withCredentials');
  if (withCreds) {
    options.withCredentials = true;
  }
  const csrfToken = yield select(getConfigKey, 'csrfToken');

  return configureOptions(options, { csrfToken });
}

/**
 * Calls napi, this one you probably don't need, use puppy instead
 */
export function* requestNapi(
  options: XhrRequestOptions,
  fakeResponse?: (requestOptions: AxiosRequestConfig) => any
): any {
  // Routing
  // local dev goes to sharedApiBasePath which hits the express middlewares locally
  // xhr local goes to the VM
  // canonical goes to the xhr-server
  if (options.baseURL === undefined) {
    // Handle prod vs local endpoint routing
    if (process.env.NODE_ENV !== 'production' || process.env.XHR_ENV === 'vm') {
      options.baseURL = yield select(getConfigKey, 'sharedApiBasePath');
      // Local full docker helper
    } else if (window['__XHR_LOCAL__']) {
      options.baseURL = LOCAL_XHR_NAPI_ENDPOINT;
      // Production goes to the xhr server
    } else {
      options.baseURL = CANONICAL_XHR_NAPI_ENDPOINT;
    }
  }

  options = yield call(configureOptionsAndIncludeAuthFromRedux, options);

  const fake = yield call(runFake, fakeResponse, options);
  if (fake) {
    return fake;
  }

  return yield call(getApiResponse, options);
}

export function* configurePuppyOptionsAndIncludeAuthFromRedux(
  options: XhrRequestOptions,
  papiBase?: string
) {
  const withCreds = yield select(getConfigKey, 'withCredentials');
  if (withCreds) {
    options.withCredentials = true;
  }
  const csrfToken = yield select(getConfigKey, 'csrfToken');
  return configurePuppyOptions(options, { csrfToken }, papiBase);
}

/**
 * Calls puppy, this is the one you want in 99% of cases
 */
export function* requestPuppy(
  options: XhrRequestOptions,
  fakeResponse?: (requestOptions: XhrRequestOptions) => any,
  papiBase: string = 'dashared'
): any {
  options = yield call(configurePuppyOptionsAndIncludeAuthFromRedux, options, papiBase);

  const fake = yield call(runFake, fakeResponse, options);
  if (fake) {
    return fake;
  }

  return yield call(getApiResponse, options);
}
